/**
 * Version checker to ensure users always have the latest version
 * This prevents cached old UI/UX from being served
 *
 * The build identity comes from src/config.json, which is stamped at deploy
 * time by scripts/prepare-prod-config.js (branch, commit sha, commit date).
 * The same script writes public/meta.json, which ships to /meta.json on the
 * deployed site — polling it is how a tab that was left open notices that a
 * newer build went out.
 *
 * Local dev has neither buildCommit nor /meta.json, so every check no-ops.
 */
import config from '../../config.json';

const BUILD_ID = config.buildCommit || '';
const VERSION_KEY = 'itpcrm_build_id';

// How often an open tab asks whether a newer build shipped.
const POLL_INTERVAL_MS = 10 * 60 * 1000;

const clearCaches = () => {
  if ('caches' in window) {
    return caches
      .keys()
      .then((names) => Promise.all(names.map((name) => caches.delete(name))))
      .catch(() => undefined);
  }
  return Promise.resolve();
};

export const checkVersion = () => {
  if (!BUILD_ID) return;
  try {
    const storedBuild = localStorage.getItem(VERSION_KEY);

    // First visit on this browser — record the build, don't reload.
    if (!storedBuild) {
      localStorage.setItem(VERSION_KEY, BUILD_ID);
      return;
    }

    if (storedBuild !== BUILD_ID) {
      console.log(`New build loaded. Was: ${storedBuild}, now: ${BUILD_ID}`);
      localStorage.setItem(VERSION_KEY, BUILD_ID);
      clearCaches();
    }
  } catch (error) {
    console.error('Error checking app version:', error);
  }
};

// Don't re-check more than once a minute — `focus` can fire in bursts.
const MIN_CHECK_GAP_MS = 60 * 1000;
let lastCheckedAt = 0;

const pollForNewBuild = () => {
  if (!BUILD_ID) return;
  const now = Date.now();
  if (now - lastCheckedAt < MIN_CHECK_GAP_MS) return;
  lastCheckedAt = now;
  fetch('/meta.json?t=' + Date.now(), { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      const latest = data && data.buildCommit;
      if (!latest || latest === BUILD_ID) return;
      console.log(`Newer build ${latest} detected (running ${BUILD_ID}), reloading...`);
      try {
        localStorage.setItem(VERSION_KEY, latest);
      } catch (e) {
        /* storage unavailable — reload anyway */
      }
      clearCaches().then(() => window.location.reload());
    })
    .catch(() => undefined);
};

export const initVersionCheck = () => {
  // Record/compare the build this tab actually loaded
  checkVersion();

  // Then watch for a newer one shipping underneath a long-lived tab
  setInterval(pollForNewBuild, POLL_INTERVAL_MS);
  window.addEventListener('focus', pollForNewBuild);
};
